import logger from './logger'

// Global state
let currentSessionId = null;
let refreshPromise = null;


/**
 * @description Saves the session ID and dispatches an event so React UI can update live
 */
export const setAPISessionService = (sessionId) => {
 const functionName = 'setAPISessionService';   
 currentSessionId = sessionId;
 if (sessionId) {
  sessionStorage.setItem('api_session_id', sessionId);
     // Tell React the token changed so UI can update
  window.dispatchEvent(new CustomEvent('session-updated', { detail: sessionId })); 
  logger.info('IAP session updated and stored securely.', { functionName });   
 } else {
  sessionStorage.removeItem('api_session_id');
  window.dispatchEvent(new CustomEvent('session-updated', { detail: null })); 
   logger.info('IAP token cleared from session.', { functionName });   
 }
};
/**
 * @description Silently calls the backend to exchange an expired token for a new one.
 * @param {string} oldToken - The expired JWT token.
 * @returns {Promise<string>} A promise that resolves with the new token.
 */
export async function silentTokenRefresh(sid) {
 const functionName = 'silentTokenRefresh';

 // Prevent multiple concurrent refreshes
 if (refreshPromise) {
  logger.info('Silent refresh already in progress. Reusing existing promise.', { functionName });
  return refreshPromise;
 }

 logger.info('Starting silent token refresh flow via backend.', { functionName });
 // Tell React that refresh has started (e.g., to show a subtle loading spinner if desired)
 window.dispatchEvent(new Event('iap-refresh-start'));

 refreshPromise = (async () => {
  try {
   const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 
     'X-Proxy-Auth-Session': sid,
     'Content-Type': 'application/json' 
    }
   });

   if (!response.ok) {
	  throw new Error(`Refresh API returned status: ${response.status}`);
	}

	logger.info("Silent token refresh successful.", { functionName });
    const data = await response.json();
    if (data.new_sid) {
        setAPISessionService(data.new_sid);
    }  
	return true;
  } catch (error) {
   logger.error('Silent token refresh failed.', { functionName, originalError: error });
   throw error;
  } finally {
   // Cleanup state and notify UI
   refreshPromise = null;
   window.dispatchEvent(new Event('iap-refresh-end'));
  }
 })();

 return refreshPromise;
}

/**
 * @description A wrapper for fetch that automatically handles 401 errors using silent refresh.
 * @param {string} url - The API endpoint to call.
 * @param {object} options - The options object for the fetch call.
 * @param {boolean} [isRetry=false] - Internal flag to prevent infinite retry loops.
 * @returns {Promise<Response>} A promise that resolves with the final API response.
 */
export async function fetchWithIap(url, options = {}, isRetry = false) {
 const functionName = 'fetchWithIap';
 
 // Grab the token from memory or storage
 const sid = currentSessionId || sessionStorage.getItem('api_session_id');

 const headers = {
  ...options.headers,
  'X-Requested-With': 'XMLHttpRequest'
 };

 //We inject ONLY the Proxy Auth Session ID into its own dedicated header
    if (sid) {
      headers['X-Proxy-Auth-Session'] = sid; 
     }

 const finalOptions = {
  ...options,
  headers
 };

 let response = await fetch(url, finalOptions);

 // Centralized 504 Gateway Timeout Handling
 if (response.status === 504) {
  logger.error(`API call to ${url} timed out (504).`, { functionName, context: { url } });
  const error = new Error('Request Timed Out: The server is taking too long to respond. Please try again.');
  error.status = 504;
  throw error;
 }

 // INTERCEPTOR: Handle 401 Unauthorized (Token Expired)
 if (response.status === 401) {
  if (isRetry) {
   const criticalError = new Error('IAP session refresh failed even after a retry.');
   logger.error(criticalError.message, { functionName, context: { url } });
   
   // Clear the dead token to force a hard re-login next time
   setAPISessionService(null);
   criticalError.status = 401;
   throw criticalError;
  }

  if (sid) {
   logger.warn('Token expired or unauthorized. Attempting silent refresh.', { functionName, context: { url } });
   
   try {
    // Await the silent backend refresh
    await silentTokenRefresh(sid);
    
    logger.info('Session refresh seems successful. Retrying original API call.', { functionName, context: { url } });
    
    // Call itself again, with isRetry = true so it doesn't loop infinitely
    return fetchWithIap(url, options, true);
    
   } catch (refreshError) {
    logger.error('The silent refresh flow failed. User must re-authenticate.', { functionName, originalError: refreshError, context: { url } });
    
    // Clear the dead token
    setAPISessionService(null);
    const error = new Error('SESSION_EXPIRED');
    error.status = 401;
    throw error;
   }
  } else {
   // Request failed with 401, but we didn't even have a token to refresh
   logger.warn('API returned 401 and no token is present to refresh.', { functionName, context: { url } });
   const error = new Error('SESSION_EXPIRED');
   error.status = 401;
   throw error;
  }
 }

 return response;
}
